import type { Career, CareerId, CriterionId, Scenario, SkillId } from '../types';
import type { ContentRegistry } from '../registry/registry';
import { getSandbox } from '../sandbox/core';

export interface ValidationIssue {
  severity: 'error' | 'warning';
  where: string;
  message: string;
}

/**
 * Phrases that would overstate what the simulator or a career track can promise. Content must
 * present guidance, never guarantees. Checked case-insensitively across career and scenario text.
 */
export const FORBIDDEN_CLAIMS: readonly string[] = [
  'guarantees employment',
  'guarantee a job',
  'guaranteed job',
  'guaranteed employment',
  'qualifies you for',
  'you will be hired',
  'will get you hired',
  'certified professional upon completion',
  'best career',
  'worst career',
];

const RANKING_PATTERNS: readonly RegExp[] = [/\bbetter career than\b/i, /\bworse career than\b/i, /\bsuperior (career|role)\b/i, /\binferior (career|role)\b/i];

function careerTexts(c: Career): string[] {
  return [c.description, ...c.responsibilities, ...c.prerequisites.map((p) => p.description), ...c.progression.flatMap((p) => [p.description, ...p.typicalFocus]), ...(c.commonNextRoles ?? [])];
}

function scenarioTexts(s: Scenario): string[] {
  return [s.summary, s.briefing, ...s.sharedInformation.map((x) => x.body), ...s.steps.map((st) => st.prompt)];
}

export function validateCareer(registry: ContentRegistry, c: Career): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const where = `career:${c.id}`;
  const err = (message: string) => issues.push({ severity: 'error', where, message });
  const warn = (message: string) => issues.push({ severity: 'warning', where, message });

  if (!c.description.trim()) err('missing description');
  if (c.responsibilities.length < 3) err('needs at least 3 responsibilities');
  (['foundational', 'intermediate', 'advanced'] as const).forEach((lvl) => {
    if (c.skills[lvl].length === 0) err(`no ${lvl} skills`);
    c.skills[lvl].forEach((s) => registry.findSkill(s) || err(`unknown skill ${s} in ${lvl}`));
  });
  const all = [...c.skills.foundational, ...c.skills.intermediate, ...c.skills.advanced];
  const dupes = all.filter((s, i) => all.indexOf(s) !== i);
  if (dupes.length) err(`skill listed at more than one level: ${[...new Set(dupes)].join(', ')}`);
  if (c.prerequisites.length === 0) err('no prerequisites (guidance) listed');
  c.tools.forEach((t) => registry.findTool(t) || err(`unknown tool ${t}`));
  if (c.tools.length === 0) err('no workplace tools listed');
  if (c.deliverables.length === 0) err('no practical deliverables listed');
  if (c.evaluationCriteria.length === 0) err('no evaluation criteria listed');
  c.evaluationCriteria.forEach((ec) => ec.skillIds.forEach((s) => registry.findSkill(s) || err(`criterion ${ec.id} references unknown skill ${s}`)));
  if (c.progression.length < 4) err('progression must cover all four tiers');
  c.relatedCareers.forEach((r) => registry.findCareer(r) || err(`unknown related career ${r}`));
  if (c.relatedCareers.includes(c.id)) err('career lists itself as related');
  if (c.relatedCareers.length === 0) warn('no related careers listed');
  if (!registry.hasCompany() || !registry.findDepartment(c.departmentId)) err(`unknown department ${c.departmentId}`);
  if (c.status === 'available' && registry.scenariosForCareer(c.id).length === 0) err('available career has no scenarios');
  if (c.status === 'available' && !registry.introScenarioForCareer(c.id)) err('available career has no introductory scenario');
  if (c.introScenarioId && !registry.findScenario(c.introScenarioId)) err(`unknown intro scenario ${c.introScenarioId}`);
  if (registry.hasCompany() && c.status === 'available' && !registry.company.employees.some((e) => e.learnerSeat && (e.careerId === c.id || e.departmentId === c.departmentId))) err('no learner seat in the company for this career');

  careerTexts(c).forEach((t) => {
    FORBIDDEN_CLAIMS.forEach((p) => t.toLowerCase().includes(p) && err(`contains forbidden claim "${p}"`));
    RANKING_PATTERNS.forEach((re) => re.test(t) && err(`contains ranking language: ${re}`));
  });
  return issues;
}

export function validateScenario(registry: ContentRegistry, s: Scenario): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const where = `scenario:${s.id}`;
  const err = (message: string) => issues.push({ severity: 'error', where, message });
  const warn = (message: string) => issues.push({ severity: 'warning', where, message });

  if (s.roles.length === 0) err('no roles');
  if (s.kind === 'cross-functional') {
    if (s.roles.length < 2) err('cross-functional scenario needs at least two roles');
    if (new Set(s.departmentIds).size < 2) err('cross-functional scenario needs at least two departments');
  }
  const roleCareers = new Set(s.roles.map((r) => r.careerId));
  if (s.variant) {
    const base = registry.findScenario(s.variant.of);
    if (String(s.variant.of) === String(s.id)) err('variant.of points at the scenario itself');
    else if (!base) err(`variant.of references unknown scenario ${s.variant.of}`);
    else if (base.variant) err(`variant.of must name a base scenario, but ${base.id} is itself a variant`);
    else {
      const baseCareers = new Set(base.roles.map((r) => String(r.careerId)));
      const mine = new Set([...roleCareers].map(String));
      const missing = [...baseCareers].filter((c) => !mine.has(c));
      const extra = [...mine].filter((c) => !baseCareers.has(c));
      if (missing.length || extra.length) err(`variant must offer the same career roles as its base ${base.id} (${[...baseCareers].join(', ')})`);
      const clash = registry
        .listScenarios()
        .find((x) => String(x.id) !== String(s.id) && x.variant && String(x.variant.of) === String(base.id) && x.variant.level === s.variant!.level);
      if (clash) err(`another variant of ${base.id} already covers the ${s.variant.level} level (${clash.id})`);
    }
  }
  s.roles.forEach((r) => {
    const career = registry.findCareer(r.careerId);
    if (!career) err(`role references unknown career ${r.careerId}`);
    if (registry.hasCompany() && !registry.findDepartment(r.departmentId)) err(`role references unknown department ${r.departmentId}`);
    if (r.criterionIds.length === 0) err(`role ${r.careerId} has no evaluation criteria`);
    r.criterionIds.forEach((cid) => s.rubric.criteria.some((c) => c.id === cid) || err(`role ${r.careerId} references unknown criterion ${cid}`));
    if (r.managerId && registry.hasCompany() && !registry.company.employees.some((e) => e.id === r.managerId)) err(`role ${r.careerId} references unknown manager ${r.managerId}`);
  });
  s.departmentIds.forEach((d) => registry.hasCompany() && !registry.findDepartment(d) && err(`unknown department ${d}`));
  s.toolIds.forEach((t) => registry.findTool(t) || err(`unknown tool ${t}`));
  s.datasetIds?.forEach((d) => registry.findDataset(d) || err(`unknown dataset ${d}`));
  s.outcomes?.forEach((o) => (o.minScore < 0 || o.minScore > 1) && err(`outcome ${o.id} has minScore outside 0..1`));
  if (s.outcomes && !s.outcomes.some((o) => o.minScore === 0)) warn('outcomes do not cover a score of 0');
  s.skillIds.forEach((sk) => registry.findSkill(sk) || err(`unknown skill ${sk}`));
  const criterionIds = new Set<CriterionId>(s.rubric.criteria.map((c) => c.id));
  const rubricSkills = new Set<SkillId>(s.rubric.criteria.flatMap((c) => c.skillIds));
  s.rubric.criteria.forEach((c) => {
    if (c.skillIds.length === 0) err(`criterion ${c.id} maps to no skills`);
    c.skillIds.forEach((sk) => registry.findSkill(sk) || err(`criterion ${c.id} references unknown skill ${sk}`));
  });
  rubricSkills.forEach((sk) => s.skillIds.includes(sk) || warn(`rubric skill ${sk} not listed in scenario.skillIds`));
  if (s.rubric.demonstratedThreshold <= 0 || s.rubric.demonstratedThreshold > 1) err('rubric.demonstratedThreshold must be in (0,1]');

  const stepIds = new Set<string>();
  const usedCriteria = new Set<CriterionId>();
  /** criterion -> roles whose steps check it (undefined roles = every role) */
  const criteriaByRole = new Map<CriterionId, Set<string> | 'all'>();
  const markUse = (cid: CriterionId, roles: CareerId[] | undefined) => {
    const cur = criteriaByRole.get(cid);
    if (cur === 'all') return;
    if (!roles) { criteriaByRole.set(cid, 'all'); return; }
    const set = cur ?? new Set<string>();
    roles.forEach((r) => set.add(r));
    criteriaByRole.set(cid, set);
  };
  const personaIds = new Set(s.personas.map((p) => p.id));
  const factIds = new Set(s.facts.map((f) => f.id));
  const seedTickets = new Set((s.seed.tickets ?? []).map((t) => t.id));
  const companyTickets = new Set(registry.hasCompany() ? registry.company.tickets.map((t) => t.id) : []);
  let currentRoles: CareerId[] | undefined;
  const checkCriterion = (cid: CriterionId, ctx: string) => {
    usedCriteria.add(cid);
    markUse(cid, currentRoles);
    if (!criterionIds.has(cid)) err(`${ctx} references unknown criterion ${cid}`);
  };
  s.steps.forEach((st) => {
    currentRoles = st.roles;
    if (stepIds.has(st.id)) err(`duplicate step id ${st.id}`);
    stepIds.add(st.id);
    st.roles?.forEach((r) => roleCareers.has(r) || err(`step ${st.id} targets career ${r} which has no role`));
    st.skillIds?.forEach((sk) => registry.findSkill(sk) || err(`step ${st.id} references unknown skill ${sk}`));
    switch (st.type) {
      case 'gather-evidence': {
        checkCriterion(st.criterionId, `step ${st.id}`);
        const relevant = st.evidence.filter((e) => e.relevant).length;
        if (relevant < st.minRelevant) err(`step ${st.id} requires ${st.minRelevant} relevant items but only ${relevant} exist`);
        if (st.evidence.length === relevant) warn(`step ${st.id} has no distractor evidence`);
        st.evidence.forEach((e) => {
          if (!registry.findTool(e.toolId)) err(`evidence ${e.id} references unknown tool ${e.toolId}`);
          e.establishes?.forEach((f) => {
            if (!factIds.has(f)) err(`evidence ${e.id} establishes unknown fact ${f}`);
          });
        });
        break;
      }
      case 'decision': {
        checkCriterion(st.criterionId, `step ${st.id}`);
        if (!st.options.some((o) => o.quality === 'best')) err(`step ${st.id} has no best option`);
        if (st.options.length < 2) err(`step ${st.id} needs at least two options`);
        st.options.forEach((o) => o.effects?.forEach((e) => {
          if ('ticketId' in e && !seedTickets.has(e.ticketId) && !companyTickets.has(e.ticketId)) err(`option ${o.id} affects unknown ticket ${e.ticketId}`);
        }));
        break;
      }
      case 'roleplay': {
        if (!personaIds.has(st.personaId)) err(`step ${st.id} references unknown persona ${st.personaId}`);
        if (st.checks.length === 0) err(`roleplay ${st.id} has no checks`);
        st.checks.forEach((c) => {
          checkCriterion(c.criterionId, `roleplay ${st.id}`);
          c.factIds?.forEach((f) => factIds.has(f) || err(`roleplay ${st.id} expects unknown fact ${f}`));
        });
        break;
      }
      case 'deliverable': {
        if (st.fields.length === 0) err(`deliverable ${st.id} has no fields`);
        if (st.checks.length === 0) err(`deliverable ${st.id} has no checks`);
        const fieldIds = new Set(st.fields.map((f) => f.id));
        st.checks.forEach((c) => {
          checkCriterion(c.criterionId, `deliverable ${st.id}`);
          if (!fieldIds.has(c.fieldId)) err(`deliverable ${st.id} check references unknown field ${c.fieldId}`);
          if (c.type === 'references-fact' && !factIds.has(c.factId)) err(`deliverable ${st.id} references unknown fact ${c.factId}`);
        });
        break;
      }
      case 'tool-action': {
        checkCriterion(st.criterionId, `step ${st.id}`);
        if (!registry.findTool(st.expectation.toolId)) err(`step ${st.id} references unknown tool ${st.expectation.toolId}`);
        const a = st.expectation.action;
        if ('ticketId' in a && !seedTickets.has(a.ticketId) && !companyTickets.has(a.ticketId)) err(`step ${st.id} targets unknown ticket ${a.ticketId}`);
        if (a.type === 'query.run') {
          if (!(s.datasetIds ?? []).includes(a.datasetId)) err(`step ${st.id} queries dataset ${a.datasetId} which the scenario does not list`);
          if (!registry.findDataset(a.datasetId)) err(`step ${st.id} references unknown dataset ${a.datasetId}`);
        }
        break;
      }
      case 'handoff': {
        checkCriterion(st.criterionId, `step ${st.id}`);
        if (registry.hasCompany()) {
          if (!registry.findDepartment(st.toDepartmentId)) err(`handoff ${st.id} targets unknown department ${st.toDepartmentId}`);
          if (!registry.company.employees.some((e) => e.id === st.toEmployeeId)) err(`handoff ${st.id} targets unknown employee ${st.toEmployeeId}`);
        }
        st.revealsFacts?.forEach((f) => factIds.has(f) || err(`handoff ${st.id} reveals unknown fact ${f}`));
        break;
      }
      case 'sandbox': {
        checkCriterion(st.criterionId, `step ${st.id}`);
        if (st.sandboxes.length === 0) err(`sandbox step ${st.id} has no sandboxes`);
        if (st.checks.length === 0) err(`sandbox step ${st.id} has no checks`);
        const ids = new Set(st.sandboxes.map((x) => x.id));
        st.checks.forEach((c) => {
          if (c.type === 'answer') err(`sandbox step ${st.id} cannot use answer checks`);
          else if (!ids.has(c.sandboxId)) err(`sandbox step ${st.id} check references unknown sandbox ${c.sandboxId}`);
        });
        st.sandboxes.forEach((sb) => {
          try {
            getSandbox(sb.kind).init(sb.initial);
          } catch (e) {
            err(`sandbox ${sb.id} in step ${st.id}: ${e instanceof Error ? e.message : String(e)}`);
          }
        });
        break;
      }
      case 'briefing':
        break;
    }
  });
  s.personas.forEach((p) => p.knownFacts.forEach((kf) => factIds.has(kf.factId) || err(`persona ${p.id} knows unknown fact ${kf.factId}`)));
  s.roles.forEach((r) => {
    const roleSteps = s.steps.filter((st) => !st.roles || st.roles.includes(r.careerId));
    if (roleSteps.filter((st) => st.type !== 'briefing').length === 0) err(`role ${r.careerId} has no assessable steps`);
    r.criterionIds.forEach((cid) => {
      const use = criteriaByRole.get(cid);
      const checked = use === 'all' || (use !== undefined && use.has(r.careerId));
      if (!checked) err(`criterion ${cid} for role ${r.careerId} is never checked by a step that role performs`);
    });
  });
  scenarioTexts(s).forEach((t) => FORBIDDEN_CLAIMS.forEach((p) => t.toLowerCase().includes(p) && err(`contains forbidden claim "${p}"`)));
  return issues;
}

export function validateCompany(registry: ContentRegistry): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  if (!registry.hasCompany()) return [{ severity: 'error', where: 'company', message: 'no company registered' }];
  const c = registry.company;
  const err = (where: string, message: string) => issues.push({ severity: 'error', where, message });
  const depIds = new Set(c.departments.map((d) => d.id));
  const empIds = new Set(c.employees.map((e) => e.id));
  c.departments.forEach((d) => {
    d.toolIds.forEach((t) => c.tools.some((x) => x.id === t) || err(`department:${d.id}`, `unknown tool ${t}`));
    if (d.headId && !empIds.has(d.headId)) err(`department:${d.id}`, `unknown head ${d.headId}`);
  });
  c.employees.forEach((e) => {
    if (!depIds.has(e.departmentId)) err(`employee:${e.id}`, `unknown department ${e.departmentId}`);
    if (e.managerId && !empIds.has(e.managerId)) err(`employee:${e.id}`, `unknown manager ${e.managerId}`);
    if (e.careerId && !registry.findCareer(e.careerId)) err(`employee:${e.id}`, `unknown career ${e.careerId}`);
  });
  c.policies.forEach((p) => depIds.has(p.ownerDepartmentId) || err(`policy:${p.id}`, `unknown owner department`));
  c.tickets.forEach((t) => {
    if (!depIds.has(t.departmentId)) err(`ticket:${t.id}`, 'unknown department');
    if (!empIds.has(t.requesterId)) err(`ticket:${t.id}`, 'unknown requester');
  });
  c.knowledgeBase.forEach((a) => depIds.has(a.departmentId) || err(`kb:${a.id}`, 'unknown department'));
  c.projects.forEach((p) => {
    if (!empIds.has(p.leadId)) err(`project:${p.id}`, 'unknown lead');
    p.departmentIds.forEach((d) => depIds.has(d) || err(`project:${p.id}`, `unknown department ${d}`));
  });
  return issues;
}

export function validateRegistry(registry: ContentRegistry): ValidationIssue[] {
  const issues = [...validateCompany(registry)];
  registry.listCareers().forEach((c) => issues.push(...validateCareer(registry, c)));
  registry.listScenarios().forEach((s) => issues.push(...validateScenario(registry, s)));
  return issues;
}
